/**
 * メニューの通知の件数（2026-11-05 ユーザー決定）
 *
 * 通知は、サーバーが行き先の画面から末端のメニューに振り分ける（services/notificationMenuService.js）。
 * 画面は、下のメニュー → ホーム・分類のページのカード → 末端のメニューの、どの段にも同じ数字を出す。
 * 末端のメニューの画面を開いたら、そのメニューの通知をまとめて既読にする（MenuNotificationReader）。
 * 件数は1回の取得をすべての部品で共有する（部品ごとに取りにいかない）。
 */
import { useEffect, useState } from 'react';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? 'http://localhost:3000' : RAILWAY_API;
};
const API_URL = getApiUrl();

// 末端のメニューの画面 → メニューの番号（サーバーの MENU_RULES のキーと合わせる）
export const MENU_KEY_BY_PATH = {
  '/question-forest': 'question_forest',
  '/desire-pool': 'desire_pool',
  '/token-spending/timeline': 'spending_feed',
  '/projects': 'projects',
  '/applications': 'applications',
  '/recruitments': 'recruitments',
  '/stores': 'stores',
  '/my-stores': 'my_stores',
  '/wallet': 'wallet',
  '/friends': 'friends',
  '/discussions': 'discussions',
  '/chat': 'chat',
  '/badges': 'badges',
  '/purchase-history': 'purchase_history',
};

// 分類（下のメニュー・ホームの分類のカード）→ その中の末端のメニュー
export const MENU_KEYS_BY_CATEGORY = {
  '/dashboard/forest': ['question_forest', 'desire_pool', 'spending_feed'],
  '/dashboard/applications': ['projects', 'applications', 'recruitments'],
  '/dashboard/projects': ['projects', 'applications', 'recruitments'],
  '/dashboard/social': ['friends', 'discussions', 'chat', 'badges'],
  '/dashboard/store': ['stores', 'my_stores', 'wallet'],
};

let state = { menus: {} };
const listeners = new Set();
let inflight = null;

const hasToken = () => Boolean(localStorage.getItem('token'));

/** 件数を取り直す（同時に呼ばれても1回にまとめる） */
export async function refreshMenuNotificationCounts() {
  if (!hasToken()) return;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/menu-counts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        state = { menus: data.menus || {} };
        listeners.forEach((listener) => listener(state));
      }
    } catch (error) {
      // 件数は補助の表示なので、取れなくても画面は止めない
      console.warn('メニューの通知の件数を取得できませんでした:', error.message);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** 末端のメニューの画面を開いたとき、そのメニューの通知をまとめて既読にする */
export async function markMenuNotificationsRead(menuKey) {
  if (!menuKey || !hasToken()) return;
  try {
    const response = await fetch(`${API_URL}/api/notifications/menu/${menuKey}/read`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
    if (response.ok) await refreshMenuNotificationCounts();
  } catch (error) {
    console.warn(`メニュー（${menuKey}）の通知を既読にできませんでした:`, error.message);
  }
}

/** 件数を使う部品のフック。countFor にメニューの番号（1つか配列）を渡すと合計を返す */
export function useMenuNotificationCounts() {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    listeners.add(setSnapshot);
    refreshMenuNotificationCounts();
    const handleVisible = () => {
      if (!document.hidden) refreshMenuNotificationCounts();
    };
    window.addEventListener('focus', handleVisible);
    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      listeners.delete(setSnapshot);
      window.removeEventListener('focus', handleVisible);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, []);

  const countFor = (keys) =>
    (Array.isArray(keys) ? keys : [keys]).reduce((sum, key) => sum + (Number(snapshot.menus[key]) || 0), 0);

  return {
    menus: snapshot.menus,
    countFor,
    /** 末端のメニューの画面（to）の件数 */
    countForPath: (to) => (MENU_KEY_BY_PATH[to] ? countFor(MENU_KEY_BY_PATH[to]) : 0),
    /** 分類（下のメニュー・分類のカード）の合計 */
    countForCategory: (route) => countFor(MENU_KEYS_BY_CATEGORY[route] || []),
  };
}
